import { createFileRoute, Link } from '@tanstack/react-router'
import * as React from 'react'

export const Route = createFileRoute('/import/$id')({
  component: ImportDetailPage,
})

type RecipeSlot = {
  id: string
  zone: string
  category: string
  brand?: string
  productName?: string
  shade?: string
  colorFamily?: string
  finish?: string
  confidence?: number
  confirmationStatus?: string
}

type KitItem = {
  id: string
  category: string
  brand?: string
  productName?: string
  colorFamily?: string
  finish?: string
  status?: string
}

type SourceImportResult = {
  id: string
  status: string
  platform?: string
  creatorHandle?: string
  title?: string
  attributionUrl?: string
  errorMessageSafe?: string
  tryOn?: {
    status: string
    resultUrl: string | null
    previewUrl: string | null
  } | null
  recipe?: RecipeSlot[]
}

const ACTIVE_STATUSES = ['created', 'resolving', 'resolved', 'analyzing', 'rendering']

type StepState = 'done' | 'active' | 'pending'
type TimelineStep = { label: string; state: StepState }

function getTimelineSteps(status: string): TimelineStep[] {
  if (status === 'created' || status === 'resolving') {
    return [
      { label: 'Reading creator tutorial', state: 'active' },
      { label: 'Detecting makeup steps', state: 'pending' },
      { label: 'Matching products to your kit', state: 'pending' },
      { label: 'Adapting to your skin context', state: 'pending' },
      { label: 'Generating try-on preview', state: 'pending' },
    ]
  }
  if (status === 'resolved') {
    return [
      { label: 'Reading creator tutorial', state: 'done' },
      { label: 'Detecting makeup steps', state: 'active' },
      { label: 'Matching products to your kit', state: 'pending' },
      { label: 'Adapting to your skin context', state: 'pending' },
      { label: 'Generating try-on preview', state: 'pending' },
    ]
  }
  // analyzing
  return [
    { label: 'Reading creator tutorial', state: 'done' },
    { label: 'Detecting makeup steps', state: 'done' },
    { label: 'Matching products to your kit', state: 'active' },
    { label: 'Adapting to your skin context', state: 'pending' },
    { label: 'Generating try-on preview', state: 'pending' },
  ]
}

function confidenceBadgeClass(confidence: number | undefined): string {
  if (confidence === undefined) return 'bg-foreground/8 text-foreground/45'
  if (confidence >= 0.7) return 'bg-success/10 text-success'
  if (confidence >= 0.4) return 'bg-amber-500/10 text-amber-700'
  return 'bg-foreground/8 text-foreground/45'
}

function confirmationInfo(status: string | undefined): { label: string; className: string } {
  const key = (status ?? 'unknown').toLowerCase().replace(/[_\s]/g, '-')
  switch (key) {
    case 'detected':
      return { label: 'Detected', className: 'bg-success/10 text-success' }
    case 'estimated':
      return { label: 'Estimated', className: 'bg-amber-500/10 text-amber-700' }
    case 'needs-review':
      return { label: 'Needs review', className: 'bg-foreground/8 text-foreground/55' }
    default:
      return { label: 'Unknown', className: 'bg-foreground/8 text-foreground/55' }
  }
}

function ImportDetailPage() {
  const { id } = Route.useParams()
  const [result, setResult] = React.useState<SourceImportResult | null>(null)
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)
  const [notFound, setNotFound] = React.useState(false)

  // Try-on trigger state
  const [triggering, setTriggering] = React.useState(false)
  const [tryOnError, setTryOnError] = React.useState<string | null>(null)

  // Kit match state
  const [showKitMatch, setShowKitMatch] = React.useState(false)
  const [kitItems, setKitItems] = React.useState<KitItem[] | null>(null)
  const [kitLoading, setKitLoading] = React.useState(false)
  const [kitError, setKitError] = React.useState<string | null>(null)

  const fetchResult = React.useCallback(async () => {
    try {
      const res = await fetch(`/api/v1/source-imports/${id}/result`)

      if (res.status === 404) {
        setNotFound(true)
        setError(null)
        setLoading(false)
        return
      }
      let data: SourceImportResult | null = null
      try {
        const body = await res.json()
        data = {
          id: body.import?.id ?? '',
          status: body.import?.status ?? 'unknown',
          platform: body.import?.platform,
          creatorHandle: body.import?.creatorHandle,
          title: body.import?.title,
          attributionUrl: body.import?.attributionUrl,
          errorMessageSafe: body.import?.errorMessageSafe,
          tryOn: body.tryOn ?? null,
          recipe: body.recipeSlots ?? [],
        }
      } catch {
        data = null
      }

      // 409 = result requested before recipe_ready; the body still carries status
      // so the timeline can render. Anything else that isn't ok is a real error.
      if (!res.ok && res.status !== 409) {
        throw new Error('Failed to fetch')
      }

      if (data) {
        setResult(data)
      }
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error')
    } finally {
      setLoading(false)
    }
  }, [id])

  React.useEffect(() => {
    fetchResult()
    // Poll for status updates while the import is still active.
    if (result?.status && ACTIVE_STATUSES.includes(result.status)) {
      const interval = setInterval(fetchResult, 3000)
      return () => clearInterval(interval)
    }
  }, [fetchResult, result?.status])

  const handleTryOn = React.useCallback(async () => {
    if (triggering) return
    setTriggering(true)
    setTryOnError(null)
    try {
      const scanId = window.localStorage.getItem('ootopia:last-uploaded-scan-id')
      if (!scanId) {
        setTryOnError('Complete a skin scan before trying on this look.')
        return
      }
      const res = await fetch(`/api/v1/source-imports/${id}/try-on`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ scanId }),
      })
      if (res.ok || res.status === 202) {
        // Refetch immediately; polling will track rendering -> ready.
        await fetchResult()
      } else if (res.status === 409 || res.status === 422) {
        setTryOnError('Complete a skin scan before trying on this look.')
      } else if (res.status === 503) {
        setTryOnError('Try-on is temporarily disabled.')
      } else {
        setTryOnError('Unable to start try-on. Please try again.')
      }
    } catch {
      setTryOnError('Unable to connect. Please try again.')
    } finally {
      setTriggering(false)
    }
  }, [id, triggering, fetchResult])

  const handleMatchKit = React.useCallback(async () => {
    setShowKitMatch(true)
    if (kitItems !== null || kitLoading) return
    setKitLoading(true)
    setKitError(null)
    try {
      const res = await fetch('/api/v1/kit')
      if (!res.ok) throw new Error('Failed to load kit')
      const data = (await res.json()) as { items?: KitItem[] }
      setKitItems(data.items ?? [])
    } catch (err) {
      setKitError(err instanceof Error ? err.message : 'Unknown error')
    } finally {
      setKitLoading(false)
    }
  }, [kitItems, kitLoading])

  const retryFetch = React.useCallback(() => {
    setError(null)
    setNotFound(false)
    setLoading(true)
    fetchResult()
  }, [fetchResult])

  // --- Loading ---
  if (loading && !result) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh] gap-4">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        <p className="text-sm text-muted">Loading...</p>
      </div>
    )
  }

  // --- Error ---
  if (error && !result) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh] gap-4">
        <div className="rounded-lg bg-danger/10 p-4 text-center">
          <p className="text-danger font-medium">Failed to load. Try again.</p>
        </div>
        <button
          type="button"
          onClick={retryFetch}
          className="rounded-lg bg-primary px-4 py-2 text-sm text-primary-foreground"
        >
          Try Again
        </button>
      </div>
    )
  }

  // --- Not found ---
  if (notFound) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh] gap-4">
        <p className="text-sm text-muted">Import not found.</p>
        <Link
          to="/import"
          className="rounded-lg bg-primary px-4 py-2 text-sm text-primary-foreground"
        >
          New import
        </Link>
      </div>
    )
  }

  if (!result) {
    return null
  }

  const status = result.status

  // --- Progress timeline (created / resolving / resolved / analyzing) ---
  if (
    status === 'created' ||
    status === 'resolving' ||
    status === 'resolved' ||
    status === 'analyzing'
  ) {
    const steps = getTimelineSteps(status)
    return (
      <div className="ootopia-pwa-page ootopia-pwa-stack">
        <div>
          <p className="ootopia-section-kicker">Importing your look</p>
          <h1 className="mt-2 text-4xl font-black tracking-[-0.06em] ootopia-editorial-heading">
            Analyzing tutorial
          </h1>
          <p className="mt-3 text-sm text-muted">
            We are reading the video, detecting the makeup steps, and extracting a recipe.
          </p>
        </div>

        <div className="rounded-2xl ootopia-card-premium p-6">
          <ol className="flex flex-col gap-5">
            {steps.map((step, index) => (
              <li key={step.label} className="flex items-center gap-4">
                <span
                  className={`grid h-9 w-9 shrink-0 place-items-center rounded-full ${
                    step.state === 'done'
                      ? 'bg-success/15 text-success'
                      : step.state === 'active'
                        ? 'bg-primary/10 text-primary'
                        : 'bg-foreground/8 text-foreground/40'
                  }`}
                >
                  {step.state === 'done' ? (
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <title>Done</title>
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  ) : null}
                  {step.state === 'active' ? (
                    <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary" />
                  ) : null}
                  {step.state === 'pending' ? (
                    <span className="text-xs font-bold">{index + 1}</span>
                  ) : null}
                </span>
                <span
                  className={`text-sm font-medium ${
                    step.state === 'pending' ? 'text-foreground/45' : 'text-foreground'
                  }`}
                >
                  {step.label}
                </span>
              </li>
            ))}
          </ol>
        </div>

        <Link to="/import" className="text-sm font-semibold text-muted">
          Back to import
        </Link>
      </div>
    )
  }

  // --- Recipe ready ---
  if (status === 'recipe_ready') {
    return (
      <div className="ootopia-pwa-page ootopia-pwa-stack">
        <div>
          <p className="ootopia-section-kicker">Recipe ready</p>
          <h1 className="mt-2 text-4xl font-black tracking-[-0.06em] ootopia-editorial-heading">
            {result.title ?? 'Imported look'}
          </h1>
        </div>

        {/* Import info / attribution */}
        <div className="rounded-2xl ootopia-card-premium p-6">
          <div className="flex flex-col gap-2">
            {result.creatorHandle ? (
              <p className="text-sm text-muted">By {result.creatorHandle}</p>
            ) : null}
            {result.attributionUrl ? (
              <a
                href={result.attributionUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-semibold text-primary underline"
              >
                View original
              </a>
            ) : null}
          </div>
        </div>

        {/* Recipe slots */}
        <div>
          <h2 className="mb-3 font-semibold">Products detected from the video</h2>
          {!result.recipe || result.recipe.length === 0 ? (
            <div className="rounded-xl border bg-card p-6 text-center shadow-sm">
              <p className="text-sm text-muted">No recipe slots were extracted yet.</p>
            </div>
          ) : (
            <div className="grid gap-3">
              {result.recipe.map((slot) => {
                const conf = confirmationInfo(slot.confirmationStatus)
                const matchedItem =
                  kitItems && showKitMatch
                    ? kitItems.find((item) => item.category === slot.category)
                    : undefined
                return (
                  <div key={slot.id} className="rounded-xl border bg-card p-4 shadow-sm">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex flex-col gap-1">
                        <span className="text-xs font-semibold uppercase tracking-wide text-muted">
                          {slot.zone.charAt(0).toUpperCase() + slot.zone.slice(1)}
                        </span>
                        <span className="font-medium">
                          {slot.brand || slot.productName || 'Product details unavailable'}
                        </span>
                        <span className="text-sm text-muted">
                          {slot.category.charAt(0).toUpperCase() + slot.category.slice(1)}
                          {slot.shade ? ` - ${slot.shade}` : ''}
                          {slot.finish
                            ? ` - ${slot.finish.charAt(0).toUpperCase() + slot.finish.slice(1)}`
                            : ''}
                        </span>
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        {slot.confidence !== undefined ? (
                          <span
                            className={`rounded-full px-2.5 py-1 text-xs font-semibold ${confidenceBadgeClass(slot.confidence)}`}
                          >
                            {Math.round(slot.confidence * 100)}%
                          </span>
                        ) : null}
                        <span
                          className={`rounded-full px-2.5 py-1 text-xs font-semibold ${conf.className}`}
                        >
                          {conf.label}
                        </span>
                        {slot.confidence !== undefined && slot.confidence < 1 ? (
                          <span className="text-[10px] font-medium text-muted">
                            Estimated from visual evidence
                          </span>
                        ) : null}
                      </div>
                    </div>

                    {/* Kit match indicator */}
                    {showKitMatch && kitItems !== null && !kitLoading ? (
                      <div className="mt-3 border-t border-foreground/8 pt-3">
                        {matchedItem ? (
                          <span className="inline-flex items-center gap-2 text-xs font-semibold text-success">
                            <span className="rounded-full bg-success/10 px-2 py-0.5">
                              In your kit
                            </span>
                            {matchedItem.brand ?? matchedItem.productName ?? 'Matched item'}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-2 text-xs font-semibold text-foreground/55">
                            <span className="rounded-full bg-foreground/8 px-2 py-0.5">
                              Not in kit
                            </span>
                          </span>
                        )}
                      </div>
                    ) : null}
                  </div>
                )
              })}
            </div>
          )}
        </div>

        {/* Kit match status panel */}
        {showKitMatch ? (
          <div className="rounded-xl border bg-muted/30 p-4">
            {kitLoading ? (
              <div className="flex items-center gap-3">
                <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary" />
                <p className="text-sm text-muted">Loading your kit...</p>
              </div>
            ) : kitError ? (
              <div className="flex flex-col gap-2">
                <p className="text-sm font-medium text-danger">{kitError}</p>
                <button
                  type="button"
                  onClick={() => {
                    setKitItems(null)
                    handleMatchKit()
                  }}
                  className="self-start rounded-lg bg-primary px-3 py-1.5 text-xs text-primary-foreground"
                >
                  Try Again
                </button>
              </div>
            ) : kitItems && kitItems.length === 0 ? (
              <p className="text-sm text-muted">
                Your kit is empty. Add products to see which detected items you already own.
              </p>
            ) : (
              <p className="text-sm text-muted">
                Showing match indicators for each detected product against your kit.
              </p>
            )}
          </div>
        ) : null}

        {/* Try-on error */}
        {tryOnError ? (
          <div className="rounded-lg bg-danger/10 p-4">
            <p className="text-sm font-medium text-danger">{tryOnError}</p>
            {tryOnError.includes('skin scan') ? (
              <a
                href={`/capture?returnTo=${encodeURIComponent(`/import/${id}`)}`}
                className="mt-3 inline-flex rounded-lg bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground"
              >
                Capture skin scan
              </a>
            ) : null}
          </div>
        ) : null}

        {/* Actions */}
        <div className="flex flex-col gap-3 sm:flex-row">
          <button
            type="button"
            onClick={handleTryOn}
            disabled={triggering}
            className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-opacity disabled:cursor-not-allowed disabled:opacity-50"
          >
            {triggering ? (
              <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-foreground" />
            ) : null}
            {triggering ? 'Starting...' : 'Try this adapted look'}
          </button>
          <button
            type="button"
            onClick={handleMatchKit}
            disabled={kitLoading}
            className="inline-flex flex-1 items-center justify-center rounded-xl border border-primary px-6 py-3 text-sm font-semibold text-primary transition-opacity disabled:opacity-50"
          >
            Match to My Kit
          </button>
        </div>

        <Link to="/import" className="text-sm font-semibold text-muted">
          Back to import
        </Link>
      </div>
    )
  }

  // --- Rendering ---
  if (status === 'rendering') {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh] gap-4">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        <p className="text-sm text-muted">Generating try-on preview...</p>
      </div>
    )
  }

  // --- Ready (try-on result) ---
  if (status === 'ready') {
    const previewUrl = result.tryOn?.previewUrl ?? null
    const resultUrl = result.tryOn?.resultUrl ?? null
    const imageSrc = previewUrl ?? resultUrl

    return (
      <div className="ootopia-pwa-page ootopia-pwa-detail">
        <div>
          <p className="ootopia-section-kicker">Try-on complete</p>
          <h1 className="mt-2 text-4xl font-black tracking-[-0.06em] ootopia-editorial-heading">
            Your try-on preview
          </h1>
        </div>

        {imageSrc ? (
          <div className="rounded-xl border bg-card p-4 shadow-sm">
            <img
              src={imageSrc}
              alt="Try-on preview"
              className="w-full rounded-lg object-cover"
              onError={(e) => {
                const img = e.currentTarget
                if (img.src !== resultUrl && resultUrl) {
                  img.src = resultUrl
                } else {
                  img.style.display = 'none'
                }
              }}
            />
          </div>
        ) : (
          <div className="rounded-xl border bg-muted/30 p-6 text-center">
            <p className="text-sm text-muted">No preview available.</p>
          </div>
        )}

        {/* AI disclosure */}
        <div className="rounded-xl border bg-muted/30 p-4">
          <p className="text-xs leading-relaxed text-muted">
            <strong>AI-Generated Cosmetic Preview:</strong> This image shows a simulated makeup
            application based on the extracted recipe. Actual results may vary depending on product
            application, lighting, and skin conditions.
          </p>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row">
          <Link
            to="/import"
            className="inline-flex flex-1 items-center justify-center rounded-xl bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground"
          >
            Try another look
          </Link>
          {imageSrc ? (
            <Link
              to="/publish/$id"
              params={{ id }}
              search={{ type: 'source-import' }}
              className="inline-flex flex-1 items-center justify-center rounded-xl border border-primary px-6 py-3 text-sm font-semibold text-primary"
            >
              Publish look
            </Link>
          ) : null}
        </div>
      </div>
    )
  }

  // --- Failed ---
  if (status === 'failed') {
    return (
      <div className="ootopia-pwa-page ootopia-pwa-stack">
        <div className="rounded-xl border bg-card p-6 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="rounded-full bg-danger/10 p-1">
              <svg
                className="h-4 w-4 text-danger"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <title>Error</title>
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </div>
            <h1 className="font-semibold">Unable to process this video.</h1>
          </div>
          {result.errorMessageSafe ? (
            <p className="mt-3 text-sm text-muted">{result.errorMessageSafe}</p>
          ) : null}
        </div>
        <Link
          to="/import"
          className="inline-flex items-center justify-center rounded-xl bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground"
        >
          Try another URL
        </Link>
      </div>
    )
  }

  // --- Unsupported ---
  if (status === 'unsupported') {
    return (
      <div className="ootopia-pwa-page ootopia-pwa-stack">
        <div className="rounded-xl border bg-card p-6 text-center shadow-sm">
          <p className="font-medium">This link cannot be analyzed yet</p>
          <p className="mt-2 text-sm text-muted">
            Please use a TikTok, Instagram Reels, or YouTube Shorts link.
          </p>
        </div>
        <Link
          to="/import"
          className="inline-flex items-center justify-center rounded-xl bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground"
        >
          Try another URL
        </Link>
      </div>
    )
  }

  // --- Expired ---
  if (status === 'expired') {
    return (
      <div className="ootopia-pwa-page ootopia-pwa-stack">
        <div className="rounded-xl border bg-card p-6 text-center shadow-sm">
          <p className="font-medium">This import has expired.</p>
        </div>
        <Link
          to="/import"
          className="inline-flex items-center justify-center rounded-xl bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground"
        >
          New import
        </Link>
      </div>
    )
  }

  // --- Fallback: treat unknown active statuses as an in-progress timeline ---
  const fallbackSteps = getTimelineSteps('created')
  return (
    <div className="ootopia-pwa-page ootopia-pwa-stack">
      <div className="rounded-2xl ootopia-card-premium p-6">
        <ol className="flex flex-col gap-5">
          {fallbackSteps.map((step, index) => (
            <li key={step.label} className="flex items-center gap-4">
              <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-foreground/8 text-foreground/40">
                <span className="text-xs font-bold">{index + 1}</span>
              </span>
              <span className="text-sm font-medium text-foreground/45">{step.label}</span>
            </li>
          ))}
        </ol>
      </div>
      <Link to="/import" className="text-sm font-semibold text-muted">
        Back to import
      </Link>
    </div>
  )
}
